/**
 * Die Rückblende im Essenz-Spiegel — die Bausteine der Seite.
 * (25.09.2026, Ticket 2560)
 *
 * Martin zum Entwurf: *„Ja, sieht toll aus. Du kannst loslegen.“*
 *
 * Zwei Teile, beide an die bestehende Verlaufsliste angebaut, keine zweite
 * Liste daneben (app.ts, `renderHistoryInto` / `showDay`):
 *   - die ZEILE eines Tages: Datumsspalte mit Linie, Karte mit Verfassung
 *     (fünf Punkte + Wort), dem Tag in einer Zeile, dem Spiegelsatz und —
 *     nur wenn es sie gibt — „Morgenmeditation · N Min“;
 *   - die Ansicht EINES Tages: Verfassung (mit Morgen/Mittag/Abend), der Tag
 *     in Kürze, der Spiegel mit „Spiegel anhören“, die Meditation dieses
 *     Morgens und Vor/Zurück.
 *
 * ⚠️ Was es für einen Tag nicht gibt, steht nicht da — kein „–“, kein
 *    „keine Angabe“. Eine leere Stelle, die sich als Angabe ausgibt, ist
 *    die halbherzige Anzeige, die Martin schon öfter beanstandet hat.
 *
 * ⚠️ Nur `textContent` für Inhalte. Die Sätze kommen aus Modell-Texten; als
 *    HTML eingesetzt wären sie ein Einfallstor.
 */
import {
  datumsSpalte,
  nachbarBeschriftung,
  tagesTitel,
  type Rueckblende,
  type Tagesphase,
  type ZeitleistenZeile,
} from "../../shared/spiegel-zeitleiste.js";
import { zeichen } from "./zeichen.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  klasse: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Fünf Punkte, `punkte` davon gefüllt — für Vorleser als ein Satz. */
function bauePunkte(punkte: number): HTMLSpanElement {
  const wrap = el("span", "zl-punkte");
  const reihe = el("span", "zl-punkte-reihe");
  reihe.setAttribute("aria-hidden", "true");
  for (let i = 1; i <= 5; i++) {
    const p = el("span", "zl-punkt");
    if (i <= punkte) p.dataset.voll = "1";
    reihe.appendChild(p);
  }
  wrap.append(reihe, el("span", "zl-sr", `Verfassung ${punkte} von 5`));
  return wrap;
}

/** Punkte und Wort in einer Zeile — oder null, wenn der Tag beides nicht hat. */
function baueVerfassung(z: Pick<ZeitleistenZeile, "punkte" | "wort">): HTMLSpanElement | null {
  if (z.punkte == null && !z.wort) return null;
  const zeile = el("span", "zl-verfassung");
  if (z.punkte != null) zeile.appendChild(bauePunkte(z.punkte));
  if (z.wort) zeile.appendChild(el("span", "zl-wort", z.wort));
  return zeile;
}

/** Die Datumsspalte: „DO“ / „24“ / „Sept.“ und die Linie zum nächsten Tag. */
export function fuelleDatumsSpalte(spalte: HTMLElement, day: string, langesDatum: string): void {
  const d = datumsSpalte(day);
  spalte.replaceChildren();
  if (!d) {
    spalte.textContent = langesDatum;
    return;
  }
  spalte.classList.add("zl-datum");
  // Vorleser hören das ganze Datum statt „DO 24 Sept.“.
  spalte.append(
    el("span", "zl-sr", langesDatum),
    el("span", "zl-datum-wt", d.wochentag),
    el("span", "zl-datum-zahl", d.zahl),
    el("span", "zl-datum-monat", d.monat),
    el("span", "zl-linie"),
  );
  for (const k of Array.from(spalte.children).slice(1)) k.setAttribute("aria-hidden", "true");
}

/**
 * Die Karte einer Zeile. `vorschau` ist das bestehende Element mit dem
 * Spiegelsatz (`.essence-history-excerpt`) — es wandert nur hinein.
 */
export function baueZeilenKarte(
  z: Partial<ZeitleistenZeile>,
  vorschau: HTMLElement,
  ungelesen: boolean,
): HTMLSpanElement {
  const karte = el("span", "zl-karte");
  const verfassung = baueVerfassung({ punkte: z.punkte ?? null, wort: z.wort ?? null });
  if (verfassung) karte.appendChild(verfassung);
  if (ungelesen) karte.appendChild(el("span", "zl-sr", "Noch nicht angehört."));
  if (z.einzeiler) karte.appendChild(el("span", "zl-einzeiler", z.einzeiler));
  karte.appendChild(vorschau);
  if (z.meditationMin) {
    const m = el("span", "zl-meditation-hinweis");
    const icon = el("span", "zl-icon");
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = zeichen("abspielen", 14);
    m.append(icon, el("span", "", `Morgenmeditation · ${z.meditationMin} Min`));
    karte.appendChild(m);
  }
  return karte;
}

const PHASEN: Array<[Tagesphase, string]> = [
  ["morgen", "Morgen"],
  ["mittag", "Mittag"],
  ["abend", "Abend"],
];

export interface TagesAnsicht {
  el: HTMLElement;
  /** Hier setzt app.ts den Spiegeltext hinein (wie bisher `interp`). */
  spiegelText: HTMLElement;
  /** Unter die Meditation: Klang, PDF und Neu spiegeln des Tages. */
  fuss: HTMLElement;
  /** Die Angaben vom Server einsetzen (null = nicht ladbar → Teile bleiben weg). */
  setzeRueckblende: (r: Rueckblende | null) => void;
  /** Vor/Zurück einsetzen, sobald die Reihe der Tage bekannt ist. */
  setzeNachbarn: (n: { aelter: string | null; neuer: string | null }) => void;
}

function abschnitt(titel: string, klasse: string): HTMLElement {
  const s = el("section", `zl-abschnitt ${klasse}`);
  s.appendChild(el("h3", "zl-abschnitt-titel", titel));
  return s;
}

/**
 * Die Ansicht EINES Tages. Der Spiegel steht sofort (sein Text kommt wie
 * bisher nach); Verfassung, Kürze und Meditation kommen mit der Rückblende.
 */
export function baueTagesAnsicht(o: {
  day: string;
  onSpiegelAnhoeren: () => void;
  /** Vor dem Start der Meditation — z. B. den Spiegel anhalten. */
  vorMeditation?: () => void;
  onGeheZu: (day: string) => void;
}): TagesAnsicht {
  const wurzel = el("article", "zl-tag");
  const kopf = el("header", "zl-tag-kopf");
  const titel = el("h2", "zl-tag-titel", tagesTitel(o.day));
  // Ziel für den Fokus nach Vor/Zurück — sonst landet er am Seitenanfang.
  titel.tabIndex = -1;
  kopf.append(
    titel,
    el("p", "zl-tag-unter", "Rückblende auf einen Tag"),
  );
  wurzel.appendChild(kopf);

  // Platzhalter in der festen Reihenfolge des Entwurfs; leer = unsichtbar.
  const verfassung = abschnitt("Verfassung", "zl-abschnitt--verfassung");
  const kurz = abschnitt("Der Tag in Kürze", "zl-abschnitt--kurz");
  verfassung.hidden = true;
  kurz.hidden = true;

  const spiegel = abschnitt("Essenz-Spiegel", "zl-abschnitt--spiegel");
  const anhoeren = el("button", "btn btn--ghost btn--sm zl-abspielen");
  anhoeren.type = "button";
  const aIcon = el("span", "zl-icon");
  aIcon.setAttribute("aria-hidden", "true");
  aIcon.innerHTML = zeichen("abspielen", 14);
  anhoeren.append(aIcon, el("span", "", "Spiegel anhören"));
  anhoeren.addEventListener("click", () => o.onSpiegelAnhoeren());
  const spiegelText = el("div", "hd-interpretation essence-text zl-spiegel-text");
  spiegel.append(anhoeren, spiegelText);

  const meditation = abschnitt("Morgenmeditation dieses Tages", "zl-abschnitt--meditation");
  meditation.hidden = true;

  const fuss = el("div", "zl-fuss");
  const nav = el("nav", "zl-nav");
  nav.setAttribute("aria-label", "Tage");
  nav.hidden = true;

  wurzel.append(verfassung, kurz, spiegel, meditation, nav, fuss);

  const setzeRueckblende = (r: Rueckblende | null): void => {
    verfassung.replaceChildren(verfassung.firstElementChild as Element);
    kurz.replaceChildren(kurz.firstElementChild as Element);
    meditation.replaceChildren(meditation.firstElementChild as Element);
    verfassung.hidden = kurz.hidden = meditation.hidden = true;
    if (!r) return;

    const zeile = baueVerfassung(r);
    const phasen = PHASEN.filter(([p]) => r.phasen?.[p]);
    if (zeile || phasen.length) {
      if (zeile) verfassung.appendChild(zeile);
      if (phasen.length) {
        const dl = el("dl", "zl-phasen");
        for (const [p, name] of phasen) {
          const paar = el("div", "zl-phase");
          paar.append(el("dt", "", name), el("dd", "", r.phasen[p] ?? ""));
          dl.appendChild(paar);
        }
        verfassung.appendChild(dl);
      }
      verfassung.hidden = false;
    }

    if (r.kurz.length) {
      const ul = el("ul", "zl-kurz");
      for (const s of r.kurz) ul.appendChild(el("li", "", s));
      kurz.appendChild(ul);
      kurz.hidden = false;
    }

    /*
     * 26.09.2026 (Ticket 2560): Nach 14 Tagen ist der Ton weggeräumt, der
     * Text bleibt. Dann steht die Handlung da, aber kein „Anhören“, das ins
     * Leere führt — was es nicht gibt, steht nicht da.
     */
    const anhoerbar = r.meditation?.anhoerbar !== false;
    if (r.meditation && (anhoerbar || r.meditation.handlung)) {
      if (r.meditation.handlung) {
        meditation.appendChild(el("p", "zl-handlung", r.meditation.handlung));
      }
      meditation.hidden = false;
    }
    if (r.meditation && anhoerbar) {
      const ort = el("div", "zl-meditation-ort");
      const knopf = el("button", "btn btn--ghost btn--sm zl-abspielen");
      knopf.type = "button";
      const mIcon = el("span", "zl-icon");
      mIcon.setAttribute("aria-hidden", "true");
      mIcon.innerHTML = zeichen("abspielen", 14);
      const min = r.meditation.minuten;
      knopf.append(
        mIcon,
        el("span", "", min ? `Anhören · ${min} ${min === 1 ? "Minute" : "Minuten"}` : "Anhören"),
      );
      /*
       * Derselbe Weg wie die Karte im Zuhause (Ticket 2549): app.ts setzt den
       * Player synchron in `ort` — mit DIESEM Tag, nicht dem heutigen. Der
       * Fokus geht danach auf seinen ersten Knopf, nicht an den Seitenanfang.
       */
      knopf.addEventListener("click", () => {
        o.vorMeditation?.();
        document.dispatchEvent(
          new CustomEvent("vespera:meditation-anhoeren", { detail: { ort, tag: o.day } }),
        );
        ort.querySelector<HTMLElement>("button")?.focus();
      });
      ort.appendChild(knopf);
      meditation.appendChild(ort);
    }
  };

  const setzeNachbarn = (n: { aelter: string | null; neuer: string | null }): void => {
    nav.replaceChildren();
    const knopf = (day: string, text: string, klasse: string, label: string): HTMLButtonElement => {
      const b = el("button", `zl-nav-knopf ${klasse}`, text);
      b.type = "button";
      b.setAttribute("aria-label", label);
      b.addEventListener("click", () => o.onGeheZu(day));
      return b;
    };
    if (n.aelter) {
      nav.appendChild(
        knopf(n.aelter, `‹ ${nachbarBeschriftung(n.aelter)}`, "zl-nav-knopf--aelter", `Tag davor: ${tagesTitel(n.aelter)}`),
      );
    }
    if (n.neuer) {
      nav.appendChild(
        knopf(n.neuer, `${nachbarBeschriftung(n.neuer)} ›`, "zl-nav-knopf--neuer", `Tag danach: ${tagesTitel(n.neuer)}`),
      );
    }
    nav.hidden = !n.aelter && !n.neuer;
  };

  return { el: wurzel, spiegelText, fuss, setzeRueckblende, setzeNachbarn };
}

/** Die Rückblende eines Tages holen — null, wenn es nicht geht. */
export async function ladeRueckblende(day: string, signal?: AbortSignal): Promise<Rueckblende | null> {
  try {
    const r = await fetch(`/api/essence-mirror/rueckblende?date=${encodeURIComponent(day)}`, {
      credentials: "same-origin",
      ...(signal ? { signal } : {}),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { ok?: boolean } & Partial<Rueckblende>;
    if (!j.ok || j.day !== day) return null;
    return {
      day,
      punkte: typeof j.punkte === "number" ? j.punkte : null,
      wort: typeof j.wort === "string" ? j.wort : null,
      einzeiler: typeof j.einzeiler === "string" ? j.einzeiler : null,
      meditationMin: typeof j.meditationMin === "number" ? j.meditationMin : null,
      phasen: j.phasen && typeof j.phasen === "object" ? j.phasen : {},
      kurz: Array.isArray(j.kurz) ? j.kurz.filter((s): s is string => typeof s === "string") : [],
      // Ein Server vor dem 26.09. kennt `anhoerbar` nicht — dann wie bisher.
      meditation: j.meditation ? { ...j.meditation, anhoerbar: j.meditation.anhoerbar !== false } : null,
    };
  } catch {
    return null;
  }
}
